import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, Users, UserX, AppWindow, AlertTriangle, History } from "lucide-react";

const PROFILE_BADGE = {
  a_valider: "bg-amber-100 text-amber-900 border-amber-300",
  free: "bg-slate-100 text-slate-800 border-slate-300",
  premium: "bg-amber-50 text-amber-800 border-amber-300",
  team: "bg-teal-100 text-teal-900 border-teal-300",
};
const PROFILE_LABEL = { a_valider: "À valider", free: "Free", premium: "Premium", team: "Team" };
const LEDGER_LABEL = { welcome: "Bienvenue", admin_add: "Ajout admin", debit: "Débit", refund: "Remboursement", expiration: "Expiration" };

function AppUrlRow({ app, onSaved }) {
  const [url, setUrl] = useState(app.url || "");
  const save = async () => {
    try {
      await api.patch(`/admin/applications/${app.app_id}`, { url });
      toast.success(`URL de « ${app.nom} » mise à jour.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mise à jour impossible.");
    }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`app-url-row-${app.app_id}`}>
      <span className="text-sm font-medium text-slate-700 w-32 shrink-0">{app.nom}</span>
      <Input
        placeholder="https://... (vide = non configurée)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        data-testid={`app-url-input-${app.app_id}`}
      />
      <Button variant="outline" size="sm" onClick={save} data-testid={`app-url-save-${app.app_id}`}>
        Enregistrer
      </Button>
    </div>
  );
}

export default function AdminPage() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // détail user (dialog)
  const [newApp, setNewApp] = useState({ app_id: "", nom: "" });
  const [requests, setRequests] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveProfile, setApproveProfile] = useState("free");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadOverview = useCallback(() => api.get("/admin/overview").then((r) => setOverview(r.data)), []);
  const loadApps = useCallback(() => api.get("/admin/applications").then((r) => setApps(r.data.applications)), []);
  const loadUsers = useCallback(
    (q = "") => api.get("/admin/users", { params: { search: q } }).then((r) => setUsers(r.data.users)),
    []
  );

  const loadRequests = useCallback(() => api.get("/admin/credit-requests").then((r) => setRequests(r.data.requests)), []);
  const loadVerifs = useCallback(() => api.get("/admin/verifications").then((r) => setVerifs(r.data.pending)), []);

  useEffect(() => {
    loadOverview();
    loadApps();
    loadUsers(search);
    loadRequests();
    loadVerifs();
    const interval = setInterval(() => {
      loadOverview();
      loadUsers(search);
      loadRequests();
      loadVerifs();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadOverview, loadApps, loadUsers, loadRequests, loadVerifs, search]);

  const refresh = () => {
    loadOverview();
    loadUsers(search);
    loadRequests();
    if (selected) api.get(`/admin/users/${selected.id}`).then((r) => setSelected(r.data));
  };

  const openDetail = (u) => api.get(`/admin/users/${u.id}`).then((r) => setSelected(r.data));

  const grant = async (appId, amount) => {
    try {
      const { data: r } = await api.post(`/admin/users/${selected.id}/credits`, { app_id: appId, amount });
      if (r.email_sent === false) {
        toast.warning("Les crédits ont bien été ajoutés, mais l'email de notification n'a pas pu être envoyé.");
      } else {
        toast.success(`+${amount} crédits ajoutés.`);
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ajout impossible.");
    }
  };

  const decideVerification = async (u, action, profile, reason) => {
    try {
      const { data: r } = await api.post(`/admin/users/${u.id}/verification`, { action, profile, reason });
      if (r.email_sent === false) {
        toast.warning(
          action === "approve"
            ? "Inscription validée, mais l'e-mail de confirmation n'a pas pu être envoyé."
            : "Inscription refusée, mais l'e-mail n'a pas pu être envoyé."
        );
      } else {
        toast.success(action === "approve" ? `Inscription validée (profil ${PROFILE_LABEL[profile]}).` : "Inscription refusée — e-mail envoyé à l'utilisateur.");
      }
      setApproveTarget(null);
      setRejectTarget(null);
      setRejectReason("");
      loadVerifs();
      loadUsers(search);
      loadOverview();
      if (selected?.id === u.id) {
        const { data: detail } = await api.get(`/admin/users/${u.id}`);
        setSelected(detail);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action impossible.");
    }
  };

  const grantRequest = async (req, amount) => {
    try {
      const { data: r } = await api.post(`/admin/credit-requests/${req.id}/grant`, { app_id: req.app_id, amount });
      if (r.email_sent === false) {
        toast.warning("Les crédits ont bien été ajoutés, mais l'email de notification n'a pas pu être envoyé.");
      } else {
        toast.success(`+${amount} crédits ajoutés à ${req.prenom} ${req.nom} (${req.app_nom}).`);
      }
      loadRequests();
      loadOverview();
      loadUsers(search);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ajout impossible.");
    }
  };

  const setProfile = async (profile) => {
    try {
      await api.patch(`/admin/users/${selected.id}/profile`, { profile });
      toast.success("Profil mis à jour.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mise à jour impossible.");
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/users/${selected.id}/status`, { status });
      toast.success(status === "suspendu" ? "Compte suspendu." : "Compte réactivé.");
      refresh();
    } catch {
      toast.error("Action impossible.");
    }
  };

  const setAccess = async (appId, access) => {
    try {
      await api.put(`/admin/users/${selected.id}/access`, { app_id: appId, access });
      toast.success("Accès mis à jour.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action impossible.");
    }
  };

  const addApplication = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/applications", newApp);
      toast.success("Application déclarée.");
      setNewApp({ app_id: "", nom: "" });
      loadApps();
      loadOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création impossible.");
    }
  };

  return (
    <Layout>
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mb-6" data-testid="admin-title">
        Administration
      </h1>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="verifications-block">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-800">Inscriptions à vérifier</h2>
          <Badge variant="outline" className={verifs.length ? "bg-amber-50 text-amber-800 border-amber-300 font-bold" : "bg-slate-100 text-slate-600 border-slate-300"} data-testid="verifications-count">
            {verifs.length}
          </Badge>
        </div>
        {verifs.length === 0 ? (
          <p className="px-4 py-5 text-sm text-slate-500" data-testid="verifications-empty">Aucune inscription en attente.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {verifs.map((v) => (
              <div key={v.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`verification-row-${v.id}`}>
                <div className="min-w-52">
                  <p className="font-medium text-slate-900">{v.prenom} {v.nom}</p>
                  <p className="text-xs text-slate-500">{v.email}</p>
                </div>
                <span className="text-xs text-slate-400">
                  Inscrit le {v.created_at ? new Date(v.created_at).toLocaleDateString("fr-FR") : "—"}
                </span>
                {!v.email_verified && (
                  <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300" data-testid={`verif-email-unverified-${v.id}`}>
                    Email non vérifié
                  </Badge>
                )}
                <div className="flex gap-2 ml-auto">
                  <Button
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={!v.email_verified}
                    title={!v.email_verified ? "L'utilisateur doit d'abord vérifier son adresse e-mail" : undefined}
                    onClick={() => { setApproveTarget(v); setApproveProfile("free"); }}
                    data-testid={`verif-approve-${v.id}`}
                  >
                    Valider
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    disabled={!v.email_verified}
                    title={!v.email_verified ? "L'utilisateur doit d'abord vérifier son adresse e-mail" : undefined}
                    onClick={() => { setRejectTarget(v); setRejectReason(""); }}
                    data-testid={`verif-reject-${v.id}`}
                  >
                    Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {overview && (
        <>
          {overview.pending_validation > 0 && (
            <div className="bg-amber-50 border-2 border-amber-400 text-amber-900 rounded-xl p-5 mb-6 flex items-center gap-4 shadow-sm" data-testid="pending-validation-banner">
              <AlertTriangle className="w-8 h-8 text-amber-500 shrink-0" />
              <div>
                <p className="font-bold text-lg" data-testid="pending-validation-count">
                  {overview.pending_validation} compte{overview.pending_validation > 1 ? "s" : ""} à valider
                </p>
                <p className="text-sm">Ces utilisateurs attendent la validation de leur profil.</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 shadow-sm" data-testid="stat-total-users">
              <Users className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">{overview.total_users}</p>
                <p className="text-sm text-slate-500">Utilisateurs</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 shadow-sm" data-testid="stat-suspended">
              <UserX className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">{overview.suspended}</p>
                <p className="text-sm text-slate-500">Suspendus</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 shadow-sm" data-testid="stat-active-apps">
              <AppWindow className="w-8 h-8 text-teal-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">{overview.active_apps}</p>
                <p className="text-sm text-slate-500">Applications actives</p>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8" data-testid="credit-requests-block">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-800">Demandes de crédits en attente</h2>
          <Badge variant="outline" className={requests.length ? "bg-amber-50 text-amber-800 border-amber-300 font-bold" : "bg-slate-100 text-slate-600 border-slate-300"} data-testid="credit-requests-count">
            {requests.length}
          </Badge>
        </div>
        {requests.length === 0 ? (
          <p className="px-4 py-5 text-sm text-slate-500" data-testid="credit-requests-empty">Aucune demande en attente.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {requests.map((r) => (
              <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`credit-request-row-${r.id}`}>
                <div className="min-w-52">
                  <p className="font-medium text-slate-900">{r.prenom} {r.nom}</p>
                  <p className="text-xs text-slate-500">{r.email}</p>
                </div>
                <Badge variant="outline" className={PROFILE_BADGE[r.profile]}>{PROFILE_LABEL[r.profile]}</Badge>
                <span className="text-slate-700 font-medium">{r.app_nom}</span>
                <span className="text-slate-600">Solde : <strong data-testid={`request-balance-${r.id}`}>{r.balance}</strong></span>
                <span className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : ""}</span>
                <div className="flex gap-2 ml-auto">
                  {[5, 10, 20].map((n) => (
                    <button key={n} onClick={() => grantRequest(r, n)} className="bg-teal-50 hover:bg-teal-100 text-teal-700 font-semibold border border-teal-200 text-xs px-2.5 py-1.5 rounded-md transition-colors" data-testid={`grant-request-${r.id}-${n}`}>
                      +{n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <Search className="w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher par prénom, nom ou email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              loadUsers(e.target.value);
            }}
            className="max-w-md border-0 shadow-none focus-visible:ring-0 px-0"
            data-testid="user-search-input"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Utilisateur</th>
                <th className="px-4 py-3">Inscription</th>
                <th className="px-4 py-3">Profil</th>
                <th className="px-4 py-3">Statut</th>
                {apps.map((a) => (
                  <th key={a.app_id} className="px-4 py-3 text-center">{a.nom}</th>
                ))}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                      {u.prenom} {u.nom}
                      {u.pending_request && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-xs" data-testid={`credit-request-badge-${u.id}`}>
                          Demande de crédits
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-500 text-xs">{u.email}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={PROFILE_BADGE[u.profile]} data-testid={`profile-badge-${u.id}`}>
                      {PROFILE_LABEL[u.profile]}
                    </Badge>
                    {u.verification_status === "pending" && (
                      <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-800 border-amber-300" data-testid={`verif-badge-${u.id}`}>
                        Inscription à vérifier
                      </Badge>
                    )}
                    {u.verification_status === "rejected" && (
                      <Badge variant="outline" className="ml-2 bg-red-50 text-red-800 border-red-300" data-testid={`verif-badge-${u.id}`}>
                        Inscription refusée
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className={u.status === "actif" ? "bg-teal-50 text-teal-800 border-teal-300" : "bg-orange-100 text-orange-900 border-orange-300"}
                      data-testid={`status-badge-${u.id}`}
                    >
                      {u.status === "actif" ? "Actif" : "Suspendu"}
                    </Badge>
                  </td>
                  {apps.map((a) => (
                    <td key={a.app_id} className="px-4 py-3 text-center" data-testid={`balance-${u.id}-${a.app_id}`}>
                      {u.access?.[a.app_id] ? (
                        <>
                          <span className="font-semibold text-slate-900">{u.balances?.[a.app_id] ?? 0}</span>
                          <span className="block text-xs text-slate-500" data-testid={`used-${u.id}-${a.app_id}`}>Utilisés : {u.used?.[a.app_id] ?? 0}</span>
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => openDetail(u)} data-testid={`manage-user-${u.id}`}>
                      Gérer
                    </Button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5 + apps.length} className="px-4 py-10 text-center text-slate-500" data-testid="users-empty">
                    Aucun utilisateur trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="add-app-section">
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-4">Déclarer une application</h2>
        <form onSubmit={addApplication} className="flex flex-col sm:flex-row gap-3">
          <Input placeholder="app_id (ex. seller-sim)" value={newApp.app_id} onChange={(e) => setNewApp((v) => ({ ...v, app_id: e.target.value }))} required data-testid="new-app-id-input" />
          <Input placeholder="Nom affiché" value={newApp.nom} onChange={(e) => setNewApp((v) => ({ ...v, nom: e.target.value }))} required data-testid="new-app-name-input" />
          <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="add-app-button">Ajouter</Button>
        </form>
        <div className="mt-5 space-y-2" data-testid="app-url-list">
          <p className="text-sm font-semibold text-slate-700">URL des applications (raccordement)</p>
          {apps.map((a) => (
            <AppUrlRow key={a.app_id} app={a} onSaved={loadApps} />
          ))}
        </div>
      </div>

      <Dialog open={!!approveTarget} onOpenChange={() => setApproveTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Valider l'inscription</DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {approveTarget.prenom} {approveTarget.nom} ({approveTarget.email}) recevra ses accès et ses crédits
                de bienvenue (+5 Simulation, +5 Agenda).
              </p>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">Profil à attribuer</p>
                <Select value={approveProfile} onValueChange={setApproveProfile}>
                  <SelectTrigger className="w-56" data-testid="approve-profile-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free" data-testid="approve-profile-free">Free</SelectItem>
                    <SelectItem value="premium" data-testid="approve-profile-premium">Premium</SelectItem>
                    <SelectItem value="team" data-testid="approve-profile-team">Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => decideVerification(approveTarget, "approve", approveProfile)} data-testid="approve-confirm-button">
                Confirmer la validation
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={() => setRejectTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Refuser l'inscription</DialogTitle>
          </DialogHeader>
          {rejectTarget && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {rejectTarget.prenom} {rejectTarget.nom} ({rejectTarget.email}) — choisis le motif du refus.
                Un e-mail lui sera envoyé automatiquement. Le compte est conservé et la décision est réversible.
              </p>
              <div className="space-y-2">
                {[
                  { value: "not_skool_member", label: "Membre Skool non retrouvé" },
                  { value: "identity_mismatch", label: "Nom et/ou adresse e-mail ne correspondant pas aux informations Skool" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRejectReason(opt.value)}
                    className={`w-full text-left text-sm rounded-lg border px-4 py-3 transition-colors ${
                      rejectReason === opt.value
                        ? "border-teal-500 bg-teal-50 text-teal-900 font-medium"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`reject-reason-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <Button
                className="bg-orange-600 hover:bg-orange-700"
                disabled={!rejectReason}
                onClick={() => decideVerification(rejectTarget, "reject", undefined, rejectReason)}
                data-testid="reject-confirm-button"
              >
                Confirmer le refus
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="user-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  {selected.prenom} {selected.nom}
                  <Badge variant="outline" className={PROFILE_BADGE[selected.profile]}>{PROFILE_LABEL[selected.profile]}</Badge>
                  <Badge variant="outline" className={selected.status === "actif" ? "bg-teal-50 text-teal-800 border-teal-300" : "bg-orange-100 text-orange-900 border-orange-300"} data-testid="detail-status-badge">
                    {selected.status === "actif" ? "Actif" : "Suspendu"}
                  </Badge>
                </DialogTitle>
                <p className="text-sm text-slate-500">{selected.email}</p>
              </DialogHeader>

              {selected.verification_status !== "approved" && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="detail-verif-warning">
                  <p className="text-sm text-amber-900 font-medium">
                    {selected.verification_status === "rejected"
                      ? "Inscription refusée — aucun accès ni crédit ne peut être attribué. Tu peux revalider le compte si le contrôle Skool est finalement positif."
                      : "Inscription en attente de validation Skool — le profil, les accès et les crédits ne peuvent être attribués qu'à la validation."}
                  </p>
                  <Button
                    size="sm"
                    className="mt-3 bg-teal-600 hover:bg-teal-700"
                    onClick={() => { setApproveTarget(selected); setApproveProfile("free"); }}
                    data-testid="detail-approve-button"
                  >
                    Valider l'inscription
                  </Button>
                </div>
              )}

              <div className="space-y-6 mt-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Profil</h3>
                  <Select value={selected.profile} onValueChange={setProfile} disabled={selected.verification_status !== "approved"}>
                    <SelectTrigger className="w-56" data-testid="profile-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PROFILE_LABEL).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`profile-option-${k}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Accès et crédits par application</h3>
                  <div className="space-y-3">
                    {apps.map((a) => (
                      <div key={a.app_id} className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid={`access-row-${a.app_id}`}>                        <div className="flex items-center gap-2 min-w-40">
                          <Switch
                            checked={!!selected.access?.[a.app_id]}
                            onCheckedChange={(v) => setAccess(a.app_id, v)}
                            disabled={selected.verification_status !== "approved"}
                            data-testid={`access-switch-${a.app_id}`}
                          />
                          <span className="font-medium text-slate-800">{a.nom}</span>
                        </div>
                        <span className="text-sm text-slate-600">
                          Solde : <strong data-testid={`detail-balance-${a.app_id}`}>{selected.balances?.[a.app_id] ?? 0}</strong>
                          <span className="text-slate-500"> · Utilisés : <strong data-testid={`detail-used-${a.app_id}`}>{selected.used?.[a.app_id] ?? 0}</strong></span>
                        </span>
                        <div className="flex gap-2 ml-auto">
                          {[5, 10, 20].map((n) => (
                            <button
                              key={n}
                              onClick={() => grant(a.app_id, n)}
                              disabled={selected.verification_status !== "approved"}
                              className="bg-teal-50 hover:bg-teal-100 text-teal-700 font-semibold border border-teal-200 text-xs px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-teal-50"
                              data-testid={`grant-${a.app_id}-${n}`}
                            >
                              +{n}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-sm text-slate-600" data-testid="detail-total-used">
                  Total utilisés : <strong>{Object.values(selected.used || {}).reduce((s, n) => s + n, 0)}</strong>
                </p>

                <div className="flex gap-3">
                  {selected.status === "actif" ? (
                    <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50" onClick={() => setStatus("suspendu")} data-testid="suspend-user-button">
                      Suspendre le compte
                    </Button>
                  ) : (
                    <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setStatus("actif")} data-testid="reactivate-user-button">
                      Réactiver le compte
                    </Button>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" /> Historique des mouvements
                  </h3>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="ledger-list">
                    {(selected.ledger || []).length === 0 && (
                      <p className="text-sm text-slate-500 p-4" data-testid="ledger-empty">Aucun mouvement.</p>
                    )}
                    {(selected.ledger || []).map((m) => (
                      <div key={m.id} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`ledger-entry-${m.id}`}>
                        <div>
                          <span className="font-medium text-slate-800">{LEDGER_LABEL[m.type] || m.type}</span>
                          <span className="text-slate-500 ml-2">{m.app_id}</span>
                        </div>
                        <div className="text-right">
                          <span className={m.amount >= 0 ? "text-teal-700 font-semibold" : "text-orange-700 font-semibold"}>
                            {m.amount >= 0 ? `+${m.amount}` : m.amount}
                          </span>
                          <p className="text-xs text-slate-400">{m.created_at ? new Date(m.created_at).toLocaleString("fr-FR") : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
